import { useMemo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Download, FileDown } from 'lucide-react';
import { useAlbumDownload } from '@/hooks/useAlbumDownload';
import { useTranslation } from '@/hooks/useTranslation';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import folderStatic from '@/assets/folder-static.png';
import folderStaticReady from '@/assets/folder-static-ready.png';
import folderMoving from '@/assets/folder-moving.gif';

type Props = {
  totalAssets?: number; // unflagged items count passed from admin/fotos
};

export const AlbumDownloadButton = ({ totalAssets = 0 }: Props) => {
  const { state, startDownload, triggerDownload } = useAlbumDownload();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  // Wake up n8n instance when modal opens
  useEffect(() => {
    if (isOpen) {
      try {
        fetch('https://floren8n.fly.dev/webhook/ping', { method: 'GET', mode: 'cors' }).catch(() => { });
      } catch (_) {
        // ignore wake-up errors
      }
    }
  }, [isOpen]);

  const estimatedMinutes = useMemo(() => {
    const seconds = totalAssets * 1.5;
    return Math.ceil(seconds / 60);
  }, [totalAssets]);

  const getOuterButtonLabel = () => {
    if (state.status === 'preparing') return t('preparingZip');
    if (state.status === 'ready') return t('downloadAlbum');
    return t('downloadAlbum');
  };

  const handleOuterClick = () => {
    if (state.status === 'ready') {
      triggerDownload();
    } else {
      setIsOpen(true); // idle or preparing -> open/reopen modal
    }
  };

  const handleCtaClick = () => {
    if (state.status === 'idle') {
      startDownload(); // keep modal open; image will swap to moving
    } else if (state.status === 'ready') {
      triggerDownload();
    }
  };

  const getCtaLabel = () => {
    if (state.status === 'preparing') return t('preparingZip');
    if (state.status === 'ready') return t('download');
    return t('beginDownload');
  };

  const formatExpirationDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "dd 'de' MMMM, yyyy", { locale: es });
  };

  return (
    <div className="relative">
      {state.status === 'ready' && state.expiresAt ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={handleOuterClick}
                variant="outline"
                className="w-full sm:w-auto"
              >
                <FileDown className="w-4 h-4" />
                {getOuterButtonLabel()}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {t('availableUntil', { date: formatExpirationDate(state.expiresAt) })}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Button
          onClick={handleOuterClick}
          variant="outline"
          className="w-full sm:w-auto"
        >
          {state.status === 'preparing' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4 " />
          )}
          {getOuterButtonLabel()}
        </Button>
      )}

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="w-[90vw] max-w-[320px] sm:max-w-[320px] text-center p-6">
          <DialogHeader className="items-center">
            <DialogTitle className="text-lg font-semibold">
              {state.status === 'ready' ? t('fileReadyTitle') : t('downloadModalTitle')}
            </DialogTitle>
          </DialogHeader>
          {state.status !== 'ready' && (
            <p className="text-sm text-muted-foreground mt-1">
              {t('downloadModalSubtitle')}
            </p>
          )}

          <div className="my-4 flex justify-center">
            <img
              src={state.status === 'preparing' ? folderMoving : state.status === 'ready' ? folderStaticReady : folderStatic}
              alt="folder"
              className="w-36 h-24 object-contain"
            />
          </div>

          {state.status === 'idle' && (
            <p className="text-sm text-muted-foreground">
              {t('downloadEstimatedIntro')}<br />
              {t('downloadEstimatedDetails', { minutes: estimatedMinutes, photos: totalAssets, count: estimatedMinutes })}
            </p>
          )}

          {state.status === 'preparing' && (
            <p className="text-sm text-muted-foreground">
              {t('downloadCanCloseModalHint')}
            </p>
          )}

          {state.status === 'ready' && (
            <p className="text-sm text-muted-foreground">
              {state.expiresAt && t('fileAvailableUntil', { date: formatExpirationDate(state.expiresAt) })}
            </p>
          )}

          <Button
            onClick={handleCtaClick}
            className="mt-4 w-full"
            variant="default"
          >
            {state.status === 'preparing' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            {getCtaLabel()}
          </Button>

        </DialogContent>
      </Dialog>
    </div>
  );
};
